<!-- ADORN Base Drawer Component in our overlay system -->
<!-- Usage Notes: set a fixed height for your header to keep flexbox working appropriately -->
<template>
  <SlideTransition>
    <aside
      id="drawer"
      ref="panel"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="drawerName"
      :class="[{ 'minimized': drawerMinimized }, {'open': drawerOpen }, {'scroll-locked-drawer' : isScrollLocked }, `bg-${color} flex-column app-container`]"
      :style="{'--scrollbar-width': `${scrollbarWidth}px` }"
      @keydown.esc="close">
      <div
        v-if="drawerMinimized"
        class="indicator-container">
        <div
          class="indicator mx-auto mt-xs"
          :class="darkVariant ? 'bg-white' : 'bg-armor'"/>
      </div>
      <header
        v-drag="dragDrawer()"
        :drag-options="dragOptions"
        @click="handleHeaderClick">
        <slot name="header"/>
      </header>
      <slot/>
    </aside>
  </SlideTransition>
  <div
    class="backdrop"
    :class="!drawerMinimized && !drawerIsDragging ? 'visible' : 'hidden'"
    @click="minimize"/>
</template>

<script setup>
import { computed, onMounted, useTemplateRef, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useOverlaysStore } from '@/stores/overlays.js'
import SlideTransition from '@shared/components/ADORN/transitions/SlideTransition.vue'
import { dragDirective } from '@vueuse/gesture'
import useScreenSize from '@shared/composables/screenSize'
import { useHideIntercomLauncher } from '@shared/composables/intercom.js'
import { useScrollStore } from '@shared/stores/scrollLock.js'

const overlaysStore = useOverlaysStore()
const { openDrawer, minimizeDrawer, closeDrawer } = overlaysStore
const { drawerName, drawerMinimized, drawerIsDragging } = storeToRefs(overlaysStore)
const { isDesktop } = useScreenSize()
const { isScrollLocked, scrollbarWidth } = storeToRefs(useScrollStore())

const panel = useTemplateRef('panel')
const props = defineProps({
  variant: {
    type: String,
    default: 'dark'
  },
  color: {
    type: String,
    default: 'teal'
  }
})
const dragOptions = {
  swipeDistance: [1, 1],
  useTouch: true
}
const vDrag = {
  beforeMount: dragDirective().bind
}

const darkVariant = computed(() => props.variant === 'dark')

function close() {
  closeDrawer(drawerName.value)
}
function expand() {
  openDrawer(drawerName.value, {minimized: false})
}
function minimize() {
  minimizeDrawer(drawerName.value)
}
function handleHeaderClick() {
  if (drawerMinimized.value) {
    expand()
  } else {
    minimize()
  }
}
const dragDrawer = () => ({ movement: [_, y], dragging, last }) => {
  const threshold = 5
  if (dragging) {
    drawerIsDragging.value = true
    if (panel.value) {
      const currentHeight = parseInt(panel.value.style.height)
      panel.value.style.height = `${currentHeight + (y * -1)}px`
    }
  }
  if (!dragging && last) {
    if (y < -threshold) {
      expand()
    } else if (y > threshold) {
      minimize()
    }
    setTimeout(() => {
      drawerIsDragging.value = false
    }, 300)
  }
}

useHideIntercomLauncher(() => !isDesktop.value)

const drawerOpen = ref(false)

onMounted(() => {
  drawerOpen.value = true
})
</script>

<style scoped lang="scss">
$transition: all 0.3s ease-in-out, margin-right 0s ease-in-out;
#drawer {
  // inline-flex used to work around a flexbox bug in Safari
  display: inline-flex;
  position: fixed;
  bottom: 0px;
  right: calc($spacing-xl + 52px);
  width: 391px;
  height: 0px;
  border-radius: 4px 4px 0px 0px;
  box-shadow: 0px 1px 8px 0px rgba(23, 23, 35, 0.10);
  z-index: $zindex-drawer;
  transition: $transition;
  overflow: hidden;
  @include media-tablet-or-smaller {
    right: $spacing-md;
    height: 95dvh;
    width: calc(100% - calc($spacing-md * 2));
  }
}
#drawer.minimized {
  height: 66px;
  min-height: unset;
  transition: $transition;
}
#drawer.minimized.open {
  height: 66px;
  min-height: unset;
  transition: $transition;
}
#drawer.open {
  height: 80dvh;
  min-height: 600px;
  transition: $transition;
  @include media-tablet-or-smaller {
    height: 95dvh;
  }
}
#drawer.scroll-locked-drawer {
  @include media-desktop {
    margin-right: var(--scrollbar-width);
  }
}
.backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 100dvh;
  background-color: #000;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: all 0.4s ease;
  z-index: $zindex-modal-backdrop;
  display: flex;
}

.backdrop.visible {
  opacity: 0.5;
  visibility: visible;
  pointer-events: auto;
}

.backdrop.hidden {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

.indicator-container {
  width: 100%;
}
.indicator {
  height: 2px;
  width: 150px;
  border-radius: 4px;
  transition: $transition;
}
</style>
